import { styled } from "@mui/material/styles";
import {
  AcquireBookDataGrid,
  buildSelfAcquiredInitialState,
  OTHER_USER_RESOURCE_FIELD,
  SELF_ACQUIRED_OPERATOR_VALUE,
  OTHER_USER_ACQUIRED_OPERATOR_VALUE,
} from "@/components/DataGrid";
import {
  type GridColDef,
  type GridFilterOperator,
  type GridRenderCellParams,
  type GridValueFormatterParams,
} from "@mui/x-data-grid-pro";
import { useApiClient } from "@/hooks/useApiClient";
import useSWR from "swr";
import { Stack, Typography } from "@mui/material";
import { format } from "date-fns";
import {
  isRequestCompleted,
  statusToLabel,
} from "@/features/commercialPictureAnalysis/utils";
import { type RequestDetailAndCorporations } from "@/features/commercialPictureAnalysis/types";
import CircularProgress from "@mui/material/CircularProgress";
import CheckIcon from "@mui/icons-material/Check";
import {
  forwardRef,
  type ForwardRefRenderFunction,
  useImperativeHandle,
  useContext,
} from "react";
import { ButtonVariantOption, LinkButton } from "@/components/Button";
import { SizingWrapperStyle } from "@/components/Wrapper";
import { formatNumberWithComma } from "@/utils/formatNumber";
import OutputMenu from "@/features/commercialPictureAnalysis/components/OutputMenu";
import { useReadOnlyUser } from "@/hooks/useReadOnlyUser";
import * as Sentry from "@sentry/react";
import { z } from "zod";
import { userDataContext } from "@/hooks/useUserData";

interface CommercialPictureAnalysisListGridRow
  extends RequestDetailAndCorporations {
  action?: boolean;
}

interface CommercialPictureAnalysisListAPIResponse {
  count: number;
  list: CommercialPictureAnalysisListGridRow[];
}

const otherUserResourceFilterOperators: Array<
  GridFilterOperator<CommercialPictureAnalysisListGridRow, boolean>
> = [
  {
    label: "自分で取得",
    value: SELF_ACQUIRED_OPERATOR_VALUE,
    requiresFilterValue: false,
    getApplyFilterFn:
      () =>
      ({ row }) =>
        !row.isOtherUserResource,
  },
  {
    label: "他ユーザー取得",
    value: OTHER_USER_ACQUIRED_OPERATOR_VALUE,
    requiresFilterValue: false,
    getApplyFilterFn:
      () =>
      ({ row }) =>
        row.isOtherUserResource,
  },
];

const commercialPictureAnalysisListContractSchema = z
  .object({
    list: z.array(
      z
        .object({
          isOtherUserResource: z.boolean(),
        })
        .passthrough()
    ),
  })
  .passthrough();

/**
 * 商業登記解析結果一覧APIのレスポンスが契約を満たしているか検証する。
 * @param payload APIレスポンスのペイロード
 * @param endpoint 呼び出し元のAPIエンドポイント
 * @throws {Error} API契約違反がある場合に例外を送出する
 */
const validateCommercialPictureAnalysisListContract = (
  payload: unknown,
  endpoint: string
): void => {
  const parsed = commercialPictureAnalysisListContractSchema.safeParse(payload);
  if (parsed.success) return;

  const error = new Error(`API contract violation at ${endpoint}`);
  Sentry.captureException(error, {
    extra: {
      endpoint,
      issues: parsed.error.issues,
    },
  });
  throw error;
};

const StyledDataGrid = styled(AcquireBookDataGrid)`
  // テーブルのセルをクリックしても青い枠が表示されないようにする
  & .MuiDataGrid-cell:focus-within,
  & .MuiDataGrid-cell:focus {
    outline: none !important;
  }

  // テーブルのカラムヘッダをクリックしても青い枠が表示されないようにする
  & .MuiDataGrid-columnHeader:focus-within,
  & .MuiDataGrid-columnHeader:focus {
    outline: none !important;
  }

  // テーブルヘッダーの背景色を設定
  .MuiDataGrid-columnHeaders {
    background: rgba(0, 0, 0, 0.04);
    line-height: 1.5em !important;
  }

  // テーブルタイトルを太字に
  .MuiDataGrid-columnHeaderTitle {
    font-weight: 700;
  }
`;

const makeGridColDef = (
  isReadOnly: boolean,
  isSharableUser: boolean,
  onDownloadComplete: () => void
): GridColDef[] => {
  return [
    {
      field: "taskId",
      headerName: "識別番号",
      width: 110,
      sortable: false,
      align: "right",
      headerAlign: "right",
    },
    {
      field: "identificationName",
      headerName: "識別名",
      width: 280,
      sortable: false,
      align: "left",
      headerAlign: "left",
      renderCell: ({
        value,
      }: GridRenderCellParams<string, CommercialPictureAnalysisListGridRow>) =>
        value ?? "",
    },
    {
      field: "requestedCount",
      headerName: "アップロード件数",
      width: 150,
      sortable: false,
      align: "right",
      headerAlign: "right",
      renderCell: ({
        value,
      }: GridRenderCellParams<number, CommercialPictureAnalysisListGridRow>) =>
        formatNumberWithComma(value),
    },
    {
      field: "succeededCount",
      headerName: "成功件数",
      width: 150,
      sortable: false,
      align: "right",
      headerAlign: "right",
      renderCell: ({
        value,
      }: GridRenderCellParams<number, CommercialPictureAnalysisListGridRow>) =>
        formatNumberWithComma(value),
    },
    {
      field: "createdAt",
      type: "dateTime",
      headerName: "解析依頼日時",
      width: 220,
      sortable: false,
      align: "center",
      headerAlign: "center",
      valueFormatter: (params: GridValueFormatterParams<string>) => {
        const value = params.value;
        if (value == null) return "";
        return format(new Date(value), "yyyy/MM/dd HH:mm");
      },
    },
    {
      field: OTHER_USER_RESOURCE_FIELD,
      headerName: "他ユーザー取得",
      width: 140,
      filterOperators: otherUserResourceFilterOperators,
      hide: !isSharableUser,
      sortable: false,
      align: "center",
      headerAlign: "center",
      valueFormatter: () => "",
      renderCell: ({
        value,
      }: GridRenderCellParams<boolean, CommercialPictureAnalysisListGridRow>) =>
        value ? <CheckIcon color="action" fontSize="small" /> : "",
    },
    {
      field: "status",
      headerName: "ステータス",
      width: 120,
      sortable: false,
      align: "center",
      headerAlign: "center",
      renderCell: ({ value }: GridRenderCellParams<string>) => {
        return statusToLabel(value);
      },
    },
    {
      field: "action",
      headerName: "Action",
      minWidth: 240,
      sortable: false,
      align: "center",
      headerAlign: "center",
      renderCell: ({
        id,
        row,
      }: GridRenderCellParams<
        string,
        CommercialPictureAnalysisListGridRow
      >) => {
        const isOutputEnabled =
          isRequestCompleted(row.status) && row.succeededCount > 0;
        return (
          <Stack direction="row" spacing={2}>
            <LinkButton
              disabled={!isRequestCompleted(row.status)}
              variant={ButtonVariantOption.Contained}
              wrapperSize={SizingWrapperStyle.INHERIT}
              href={`/commercialpictureanalysis/detail/${id}/`}
            >
              詳細
            </LinkButton>
            <OutputMenu
              disabled={!isOutputEnabled}
              requestId={id.valueOf() as number}
              isReadOnly={isReadOnly}
              onDownloadComplete={onDownloadComplete}
            />
          </Stack>
        );
      },
    },
  ];
};

/**
 * _FeedGrid
 * reload関数を親コンポーネントに外部公開するために、関数名にはアンダースコアをつけている
 * @param _props
 * @param ref
 */
const _FeedGrid: ForwardRefRenderFunction<{ reload: () => void }> = (
  _props,
  ref
) => {
  const isReadOnly = useReadOnlyUser();
  const userData = useContext(userDataContext);
  const isSharableUser = Boolean(userData.data.isSharable);
  const selfAcquiredInitialState =
    buildSelfAcquiredInitialState(isSharableUser);
  const { apiClient } = useApiClient();

  const fetcher = async (
    url: string
  ): Promise<CommercialPictureAnalysisListAPIResponse> => {
    const { data } =
      await apiClient.get<CommercialPictureAnalysisListAPIResponse>(url);
    validateCommercialPictureAnalysisListContract(data, url);
    return data;
  };

  const { data, isLoading, error, mutate } = useSWR<
    CommercialPictureAnalysisListAPIResponse,
    Error
  >("/commercial-picture-analyze/", fetcher, {
    revalidateOnFocus: true,
    refreshInterval: 60000,
    dedupingInterval: 30000,
  });

  const colDef = makeGridColDef(isReadOnly, isSharableUser, () => {
    mutate().catch(() => {
      // エラーハンドリングは既にuseSWRで行われているため、ここでは何もしない
    });
  });

  // refを使って外部公開する関数を定義する
  useImperativeHandle(
    ref,
    () => {
      return {
        // mutate()を使って再読み込みを行う（再取得後はSWRが再レンダーするため手動代入は不要）
        async reload() {
          await mutate();
        },
      };
    },
    []
  );

  if (error != null && data === undefined) {
    return (
      <Stack alignItems="center">
        <Typography color="error" sx={{ my: "20px" }}>
          データの取得に失敗しました。
        </Typography>
      </Stack>
    );
  }

  if (isLoading || data === undefined) {
    return (
      <Stack alignItems="center">
        <CircularProgress sx={{ my: "20px" }} />
      </Stack>
    );
  }

  const rows = data.list;

  return (
    <StyledDataGrid
      autoHeight
      disableSelectionOnClick={true}
      rows={rows}
      columns={colDef}
      initialState={selfAcquiredInitialState}
      sortModel={[
        {
          field: "id",
          sort: "asc",
        },
      ]}
    />
  );
};

/**
 * FeedGrid
 * forwardRefを使ってreload()を外部から呼び出せるようにしている
 */
export const FeedGrid = forwardRef<{ reload: () => void }>(_FeedGrid);
